import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiX } from 'react-icons/fi';

const poets = [
  {
    id: 1,
    name: 'Sören Padel',
    years: '1964–',
    category: 'modern',
    image: '/authors/padel.jpg',
    bio: 'Sören Padel ist Autor, Lektor, gelernter Historiker und Geograf. Der Berliner lebt in Schweden, schreibt Fachliteratur, Essays und Songs und ist als Verleger (vidento.eu) aber auch Webentwickler (alitero.eu) tätig. Als Gründer von Liedzeit verbindet er sein Interesse an Sprache, Klang und Reflexionen. Seine Texte entstehen aus der Beobachtung des Alltags, im kleinen wie im großen Geschehnissen und Entwicklungen, der Natur und der Geschichte.',
    quote: 'Ein guter Liedtext blüht erst mit der passenden Musik auf, auch wenn er reine Poesie ist. Bob Dylan hat uns das in Perfekton gezeigt',
    works: ['Verschiedene Liedzeit-Texte', 'Basiswissen Demografie', 'Einführung in die Demografie', 'Schwedische Kriminalfälle'],
    tag: 'Modern',
    accentColor: 'bg-amber-700',
    accentText: 'text-amber-700 dark:text-amber-400',
  },
  {
    id: 2,
    name: 'Heinrich Heine',
    years: '1797–1856',
    category: 'klassiker',
    image: '/authors/heine.jpg',
    bio: 'Heinrich Heine war einer der bedeutendsten deutschen Dichter und Schriftsteller des 19. Jahrhunderts. Sein Werk verbindet romantische Lyrik mit kritischem Witz und politischem Engagement. Gedichte wie „Die Loreley" oder „Buch der Lieder" haben bis heute eine unvergleichliche musikalische Qualität.',
    quote: 'Wo man Bücher verbrennt, verbrennt man am Ende auch Menschen.',
    works: ['Die Loreley', 'Buch der Lieder', 'Deutschland. Ein Wintermärchen', 'Atta Troll'],
    tag: 'Klassiker',
    accentColor: 'bg-stone-600',
    accentText: 'text-stone-600 dark:text-stone-400',
  },
  {
    id: 3,
    name: 'Hermann Hesse',
    years: '1877–1962',
    category: 'klassiker',
    image: '/authors/hesse.jpg',
    bio: 'Hermann Hesse war ein deutsch-schweizerischer Schriftsteller und Nobelpreisträger. Sein Werk kreist um die Suche nach dem Selbst, die Spannung zwischen Individuum und Gesellschaft und die Sehnsucht nach innerer Freiheit. Texte wie „Steppenwolf" oder „Siddhartha" haben eine lyrische Qualität, die Liedzeit inspiriert.',
    quote: 'Jedem Anfang wohnt ein Zauber inne.',
    works: ['Siddhartha', 'Der Steppenwolf', 'Narziss und Goldmund', 'Gedichte'],
    tag: 'Klassiker',
    accentColor: 'bg-stone-600',
    accentText: 'text-stone-600 dark:text-stone-400',
  },
  {
    id: 4,
    name: 'Friedrich Rückert',
    years: '1788–1866',
    category: 'klassiker',
    image: '/authors/rueckert.jpg',
    bio: 'Friedrich Rückert war ein deutscher Dichter und Orientalist, ein Meister der Sprach- und Formkunst. Seine Lyrik verbindet tiefes Gefühl mit formaler Brillanz; viele Gedichte wurden vertont, am bekanntesten die von Gustav Mahler musizierten „Kindertotenlieder".',
    quote: 'Was ist die Welt? Ein ewiges Gedicht.',
    works: ['Kindertotenlieder', 'Liebesfrühling', 'Östliche Rosen', 'Gedichte'],
    tag: 'Klassiker',
    accentColor: 'bg-stone-600',
    accentText: 'text-stone-600 dark:text-stone-400',
  },
  {
    id: 5,
    name: 'Richard Dybeck',
    years: '1811–1877',
    category: 'klassiker',
    image: '/authors/dybeck.jpg',
    bio: 'Richard Dybeck war ein schwedischer Jurist, Altertumsforscher und Dichter. Er verfasste den Text der schwedischen Nationalhymne „Du gamla, du fria". Seine Volkslieder und Balladen sind bis heute lebendiger Teil des schwedischen Liedguts.',
    quote: 'Du gamla, du fria, du fjällhöga nord.',
    works: ['Du gamla, du fria', 'Schwedische Volkslieder', 'Balladen'],
    tag: 'Klassiker',
    accentColor: 'bg-stone-600',
    accentText: 'text-stone-600 dark:text-stone-400',
  },
  {
    id: 6,
    name: 'Max Jacob',
    years: '1876–1944',
    category: 'modern',
    image: '/authors/jacob.jpg',
    bio: 'Max Jacob war ein französischer Dichter, Maler und enger Freund Picassos. Seine Lyrik verbindet christliche Mystik, volkstümliche Sprache und leisen Humor. Zahlreiche seiner Gedichte wurden vertont und zählen zu den Kostbarkeiten der französischen Chanson-Tradition.',
    quote: 'Die Poesie ist die Seele der Dinge.',
    works: ['Le Cornet à dés', 'Histoire du roi Kaboul', 'Gedichte'],
    tag: 'Modern',
    accentColor: 'bg-amber-700',
    accentText: 'text-amber-700 dark:text-amber-400',
  },
];

const filterOptions = [
  { key: 'alle', label: 'Alle' },
  { key: 'modern', label: 'Modern' },
  { key: 'klassiker', label: 'Klassiker' },
];

const PoetsPage = () => {
  const [filter, setFilter] = useState('alle');
  const [selected, setSelected] = useState(null);

  const filtered = filter === 'alle' ? poets : poets.filter(p => p.category === filter);

  const getAvatar = (name) =>
    `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&size=400&background=7a1530&color=fff&font-size=0.35`;

  return (
    <div className="bg-white dark:bg-navy-950 min-h-screen transition-colors duration-300">
      {/* Header */}
      <section className="bg-brand dark:bg-navy-900 text-white py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs font-bold tracking-widest text-amber-300 uppercase mb-3">
              Liedzeit
            </p>
            <h1 className="text-4xl md:text-6xl font-serif font-bold mb-4">
              Verfasser
            </h1>
            <p className="text-stone-300 max-w-2xl text-lg font-light">
              Die Stimmen hinter den Konzepten — von zeitlosen Klassikern bis zu zeitgenössischen Texten.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter */}
      <section className="border-b border-stone-100 dark:border-navy-800 bg-stone-50 dark:bg-navy-900 py-6 px-4">
        <div className="max-w-7xl mx-auto flex gap-3 flex-wrap">
          {filterOptions.map(opt => (
            <button
              key={opt.key}
              onClick={() => setFilter(opt.key)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                filter === opt.key
                  ? 'bg-brand text-white dark:bg-amber-600'
                  : 'bg-white dark:bg-navy-800 text-stone-600 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-navy-700 border border-stone-200 dark:border-navy-700'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filtered.map((poet, i) => (
            <motion.div
              key={poet.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07 }}
              className="group cursor-pointer bg-white dark:bg-navy-900 rounded-sm shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden"
              onClick={() => setSelected(poet)}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={poet.image || getAvatar(poet.name)}
                  alt={poet.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => { e.target.src = getAvatar(poet.name); }}
                />
              </div>
              <div className="p-6">
                <p className="text-xs font-bold tracking-widest uppercase text-stone-400 dark:text-stone-500 mb-1">
                  {poet.tag} · {poet.years}
                </p>
                <h3 className="text-xl font-serif font-bold text-stone-900 dark:text-stone-100 mb-2">
                  {poet.name}
                </h3>
                <p className="text-stone-500 dark:text-stone-400 text-sm italic line-clamp-2">
                  „{poet.quote}"
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Modal */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setSelected(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.25 }}
              className="bg-white dark:bg-navy-900 rounded-sm shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-col md:flex-row">
                <div className="w-full md:w-2/5 flex-shrink-0">
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={selected.image || getAvatar(selected.name)}
                      alt={selected.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = getAvatar(selected.name); }}
                    />
                  </div>
                </div>
                <div className="flex-1 p-8">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-xs font-bold tracking-widest uppercase text-stone-400 dark:text-stone-500 mb-1">
                        {selected.tag} · {selected.years}
                      </p>
                      <h2 className="text-2xl font-serif font-bold text-stone-900 dark:text-stone-100">
                        {selected.name}
                      </h2>
                    </div>
                    <button
                      onClick={() => setSelected(null)}
                      className="text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 transition-colors ml-4"
                      aria-label="Schließen"
                    >
                      <SafeIcon icon={FiX} className="w-5 h-5" />
                    </button>
                  </div>

                  <blockquote className="border-l-2 border-amber-400 pl-4 italic text-stone-600 dark:text-stone-300 text-sm mb-4">
                    „{selected.quote}"
                  </blockquote>

                  <p className="text-stone-600 dark:text-stone-400 text-sm leading-relaxed mb-4">
                    {selected.bio}
                  </p>

                  <div>
                    <h4 className="text-xs font-bold tracking-widest uppercase text-stone-400 dark:text-stone-500 mb-2">
                      Meilensteine
                    </h4>
                    <ul className="space-y-1">
                      {selected.works.map((w, i) => (
                        <li key={i} className="text-sm text-stone-600 dark:text-stone-400 flex items-start gap-2">
                          <span className="text-amber-600 mt-0.5">›</span> {w}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PoetsPage;
